import { useEffect, useState } from "react";
import {
  getPlatformAnalytics,
  ApiError,
  type PlatformAnalytics,
} from "../api";

/**
 * Platform-wide analytics card — sits on the curator launchpad
 * (one of the management surfaces). Same data-vs-parked split as
 * the per-org dashboard: compute what we can from existing schema,
 * surface the rest as "Coming soon" tiles.
 *
 * Lives as a card (not a full page) because it's the curator's
 * always-on launchpad signal. The per-org dashboard is the deeper
 * drill-down.
 */
export function PlatformAnalyticsCard() {
  const [data, setData] = useState<PlatformAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    getPlatformAnalytics()
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Couldn't load analytics.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div id="curator-analytics" className="card">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h3 style={{ margin: 0 }}>Platform analytics</h3>
          <p className="muted" style={{ marginTop: 4, marginBottom: 0 }}>
            Cross-org snapshot. Per-org drill-downs from the Organizations card above.
          </p>
        </div>
      </div>

      {error && (
        <div className="error" style={{ marginTop: 12 }}>{error}</div>
      )}
      {!data && !error && (
        <div className="muted" style={{ marginTop: 12 }}>Loading…</div>
      )}
      {data && (
        <>
          <div className="kpi-grid" style={{ marginTop: 12 }}>
            <KpiTile label="Organizations" value={data.metrics.orgsCount} />
            <KpiTile label="Trails" value={data.metrics.trailsCount} />
            <KpiTile
              label="Events"
              value={data.metrics.eventsCount}
              hint={
                data.metrics.eventsCancelled > 0
                  ? `${data.metrics.eventsCancelled} cancelled`
                  : undefined
              }
            />
            <KpiTile
              label="RSVPs"
              value={
                data.metrics.rsvpTotals.going +
                data.metrics.rsvpTotals.maybe +
                data.metrics.rsvpTotals.notGoing
              }
              hint={`${data.metrics.rsvpTotals.going} going`}
            />
            <KpiTile label="Users" value={data.metrics.usersCount} />
            <KpiTile label="Crew" value={data.metrics.crewCount} hint="across all orgs" />
          </div>

          <div className="kpi-grid kpi-grid-pending" style={{ marginTop: 8 }}>
            <PendingKpi
              label="Attendance"
              hint="Needs check-in flow (parked)."
            />
            <PendingKpi
              label="Volunteer hours"
              hint="Derived from attendance (parked)."
            />
            <PendingKpi
              label="Donations"
              hint="Donation log (parked)."
            />
          </div>

          {data.topOrgs.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <h4 style={{ margin: "0 0 8px" }}>Top orgs by event volume</h4>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Organization</th>
                      <th className="num">Events</th>
                      <th className="num hide-sm">Trails</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topOrgs.map((o) => (
                      <tr key={o.id}>
                        <td>
                          <div className="row" style={{ gap: 8, alignItems: "center" }}>
                            {o.logoUrl ? (
                              <img
                                src={o.logoUrl}
                                alt=""
                                className="org-mgmt-logo"
                                style={{ width: 24, height: 24 }}
                              />
                            ) : (
                              <span
                                className="org-mgmt-logo placeholder"
                                style={{ width: 24, height: 24, fontSize: 12 }}
                                aria-hidden="true"
                              >
                                🏔
                              </span>
                            )}
                            <span>{o.name}</span>
                          </div>
                        </td>
                        <td className="num">{o.eventCount}</td>
                        <td className="num hide-sm">{o.trailCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KpiTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <div className="kpi-tile">
      <div className="kpi-label muted">{label}</div>
      <div className="kpi-value">{value}</div>
      {hint && <div className="kpi-hint muted">{hint}</div>}
    </div>
  );
}

function PendingKpi({ label, hint }: { label: string; hint: string }) {
  return (
    <div className="kpi-tile kpi-tile-pending">
      <div className="kpi-label muted">{label}</div>
      <div className="kpi-value kpi-value-pending">—</div>
      <div className="kpi-hint muted">{hint}</div>
      <div className="kpi-soon">Coming soon</div>
    </div>
  );
}
